import React from 'react';

import {
  GetClientsByIdsItem,
  useGetClientsByIds,
} from '../data/useGetClientsById';
import {
  GetEntitiesByIdsItem,
  useGetEntitiesByIds,
} from '../data/useGetEntitiesByIds';
import {
  GetMemberListItem,
  useGetMembersList,
} from '../data/useGetMembersList';
import { GetJobsByIdsResult, useGetJobsByIds } from '../data/useGetJobsByIds';
import { SearchTaskItem, useSearchTasks } from '../data/useSearchTasks';
import {
  QuerySearchTasksArgs,
  searchTasksArgsToGqlSearchTasksArgs,
} from '../types/mapper';
import {
  GetJobStatusListResult,
  useGetJobStatusList,
} from '../data/useGetJobStatusList';

export interface Task extends Omit<SearchTaskItem, 'job'> {
  job: GetJobsByIdsResult & {
    client?: GetClientsByIdsItem;
    status: GetJobStatusListResult;
  };
  assignee?: GetMemberListItem;
  entity?: GetEntitiesByIdsItem;
  estimateHoursDisplay?: string;
}

// TODO double check each usage of this. I think it is being used way more than it should be, make sure they NEED the job as well
// consider adding a use-query-tasks-with-job and splitting the loading of the jobs out at least
export const useQueryTasks = ({
  variables,
  skip,
}: {
  variables?: QuerySearchTasksArgs;
  skip?: boolean;
}): {
  loading?: boolean;
  error?: boolean;
  data?: Task[];
} => {
  const members = useGetMembersList({ skip });
  const statuses = useGetJobStatusList({ skip });

  const searchVariables =
    skip || !variables
      ? undefined
      : searchTasksArgsToGqlSearchTasksArgs(variables);

  const tasks = useSearchTasks({
    variables: searchVariables,
    skip,
  });

  const jobIds = [
    ...new Set(
      tasks.data
        ?.map((x) => x.jobId)
        .filter(<T>(x: T): x is NonNullable<T> => Boolean(x))
    ),
  ];

  const jobs = useGetJobsByIds(
    !skip && jobIds && jobIds.length > 0
      ? { variables: { cloudIds: jobIds } }
      : { skip: true }
  );

  const clientIds = [
    ...new Set(
      jobs.data
        ?.map((x) => x.clientId)
        .filter(<T>(x: T): x is NonNullable<T> => Boolean(x))
    ),
  ];

  const clients = useGetClientsByIds(
    !skip && clientIds && clientIds.length > 0
      ? {
          variables: { cloudIds: clientIds },
        }
      : { skip: true }
  );

  const entityIds = [
    ...new Set(
      tasks.data
        ?.map(({ entityId }) => entityId)
        .filter(<T>(x: T): x is NonNullable<T> => Boolean(x))
    ),
  ];

  const entities = useGetEntitiesByIds(
    !skip && entityIds && entityIds.length > 0
      ? {
          variables: { cloudIds: entityIds },
        }
      : { skip: true }
  );

  return React.useMemo(() => {
    if (skip) return {};

    if (
      clients.loading ||
      entities.loading ||
      members.loading ||
      tasks.loading ||
      jobs.loading ||
      statuses.loading
    )
      return { loading: true };
    if (
      clients.error ||
      entities.error ||
      members.error ||
      tasks.error ||
      jobs.error ||
      statuses.error
    )
      return { error: true };

    const data = tasks.data?.map((task) => {
      const job = jobs.data?.find((job) => job.cloudId === task.jobId);
      const client =
        job && clients.data?.find((client) => client.cloudId === job.clientId);
      const assignee = members.data?.find(
        (member) => member.cloudId === task.assignedToUserId
      );
      const entity = entities.data?.find(
        (entity) => entity.cloudId === task.entityId
      );
      const status =
        job && statuses.data?.find((x) => x.cloudId === job.jobStatusId);
      if (job && !status) {
        console.error(
          `Query Tasks: unable to find status "${job.jobStatusId}" for job "${job.cloudId}"`
        );
      }
      const estimateHoursDisplay =
        typeof task.estimateMinutes === 'number'
          ? (task.estimateMinutes / 60).toFixed(2)
          : undefined;
      const result: Task = {
        ...task,
        job: {
          ...job!, // a task MUST have a job in the database. If we can't find it, the whole system is broken
          client,
          status: status!, // a job MUST have a status. If we can't find it, we have bigger problems
        },
        assignee,
        entity,
        estimateHoursDisplay,
      };
      return result;
    });

    return { data };
  }, [clients, entities, members, jobs, tasks, statuses, skip]);
};
